import type { StandingsResponse } from '@league/shared/contract';
import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { LINK_CLASS } from '@league/ui';

import { useHowTo } from '../../../components/HowTo';
import { Button, ErrorNote } from '../../../components/ui';
import { standingsHowTo } from '../../../content/how-to';
import { apiFetch } from '../../../lib/api';
import type { LeagueContext } from '../LeagueWorkspace';
import { useRunsOnlyNights } from '../../../lib/capabilities';
import { SegmentedControl } from '../../../components/SegmentedControl';
import { DEFAULT_SORT, nextSort, sortStandings, type StandingsSort } from './standings-sort';
import { HANDS_WON_UNAVAILABLE } from '../../../lib/hands-won';

const STANDINGS_VIEWS = [
  ['overall', 'Overall'],
  ['weekly', 'By session'],
] as const;

/**
 * The standings, derived on every read — nothing here is stored, and nothing here computes a rank
 * of its own. The engine is `computeStandings` in `@league/shared/scoring`; this renders what it
 * returns.
 *
 * ⚠ The score sheet has not arrived, so what a "point" is remains provisional. The table is honest
 * about that rather than hiding it: a director looking at a number should know how settled it is.
 */

/*
  `StandingRow` and the response envelope come from the contract — the same declaration the server's
  projection is checked against.
*/

export function StandingsTab() {
  const { league, sessions } = useOutletContext<LeagueContext>();
  /* Player profiles are the director's: an instructor sees names as text (2026-10-16). */
  const nightsOnly = useRunsOnlyNights();

  const [view, setView] = useState<'overall' | 'weekly'>('overall');
  const weeklyHeader = useRef<HTMLDivElement>(null);
  const [sort, setSort] = useState<StandingsSort>(DEFAULT_SORT);
  /* Name, one column per week, and the season total. */
  const weeklyWidth = 128 + (sessions.length + 1) * 80;
  const [copied, setCopied] = useState(false);
  const howTo = useHowTo(standingsHowTo(league.scoringConfig.mode, league.scoringConfig.tiebreakers), 'How ties are decided');

  const standings = useQuery({
    queryKey: ['standings', league.id],
    queryFn: () =>
      apiFetch<StandingsResponse>(`/api/leagues/${league.id}/standings`),
  });

  const rows = standings.data?.standings ?? [];
  /** Built by the API. The SPA has no business knowing the public site's origin. */
  const publicUrl = standings.data?.publicUrl ?? null;

  /*
    PLAYERS ON NOTHING ARE ALREADY GONE — filtered on the server, so this screen and the public page
    cannot show different people. What is left for the screen to do is say so honestly: which empty
    an empty table is, and how many people are not on a table that does have rows.
  */
  const notShown = standings.data?.notShown ?? 0;
  const silence = standings.data?.silence ?? 'not-started';

  if (standings.isPending) {
    return <p className="text-sm text-ink-3">Loading…</p>;
  }

  if (standings.isError) {
    return <ErrorNote>Could not load standings. <Button variant="secondary" size="inline" className="ml-2" onClick={() => void standings.refetch()}>Try again</Button></ErrorNote>;
  }

  return (
    <div className="flex flex-col gap-5">
      <div>
        {howTo.button}
        {howTo.panel}
      </div>
      {/*
        THE LINK SHE IS MEANT TO SHARE. Without it the public standings page exists and no
        director can find it, which is the same as not having built it. Absent until the page
        would actually render — a link that 404s is worse than no link.
      */}
      {publicUrl ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md border border-light bg-paper px-5 py-3.5">
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-medium">Share these standings</p>
            <p className="truncate text-[12px] text-ink-3">{publicUrl}</p>
          </div>
          <Button
            size="inline"
            variant="secondary"
            onClick={() => {
              void navigator.clipboard
                .writeText(publicUrl)
                .then(() => {
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                })
                /* Clipboard access can be refused. The link is on screen and selectable. */
                .catch(() => setCopied(false));
            }}
          >
            {copied ? 'Copied' : 'Copy link'}
          </Button>
          <a
            href={publicUrl}
            target="_blank"
            rel="noreferrer"
            className={`text-[13px] ${LINK_CLASS}`}
          >
            Open
          </a>
        </div>
      ) : null}

      {rows.length === 0 ? (
        /*
          Two different silences, and they send her to two different places. "Nothing entered yet"
          is the calendar; "everybody on nothing" is almost always a score sheet typed in as zeros,
          which she wants to know about today rather than in three weeks.
        */
        <p className="rounded-md border border-dashed border-light px-5 py-8 text-center text-[13px] text-ink-3">
          {silence === 'nobody-scored'
            ? `Scores are in, and everybody is still on nothing. Standings show players who have scored — worth checking ${
                notShown === 1 ? 'that sheet' : 'those sheets'
              }.`
            : 'No scores yet. Standings appear as soon as a session is entered.'}
        </p>
      ) : (
        <div className="min-w-0">
          <div className="mb-4">
            <SegmentedControl label="Standings view" options={STANDINGS_VIEWS} value={view} onChange={setView} />
          </div>
          {/*
            THE SAME CARD AS THE ROSTER — owner, 2026-10-11: the standings had become bare text on the
            page background and looked unlike every page beside it. White card, light border, rounded
            corners, 60px rows with light dividers, and the roster's small uppercase headings.
          */}
          {/* overflow-clip, not hidden or auto: it rounds the corners without breaking the pinned headings. */}
          {view === 'overall' ? <><div className="overflow-clip rounded-[14px] border border-light bg-paper">
          <table aria-label="League standings" className="w-full table-fixed border-collapse text-left">
            <thead className="sticky top-0 z-20 bg-paper">
              <tr className="h-12 border-b border-light">
                <Th className="w-16 pl-4 sm:w-20">Place</Th>
                <Th className="px-3">Player</Th>
                <Th className="w-24 px-3 text-right sm:w-32">Total points</Th>
                <Th className="w-24 pl-3 pr-4 text-right sm:w-32">Hands won</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.playerId} className="group h-[60px] border-b border-light transition-colors duration-[120ms] last:border-b-0 hover:bg-chalk">
                  {/* The leader carries the screen's one signal accent, a thin red bar (owner, 2026-10-11). Tied leaders both do. */}
                  <td className={['tabular pl-4 text-[15px] text-ink-2', row.rank === 1 ? 'border-l-[3px] border-l-signal pl-[13px]' : ''].join(' ')}>{row.rank}</td>
                  <th scope="row" className="break-words px-3 text-left [overflow-wrap:anywhere]">
                    {nightsOnly ? (
                      <span className="text-[16px] font-semibold text-ink">{row.player.firstName} {row.player.lastName}</span>
                    ) : (
                      <Link to={`/director/players/${row.playerId}`} className="text-[16px] font-semibold text-ink underline-offset-4 group-hover:underline focus-visible:outline-2 focus-visible:outline-offset-4">
                        {row.player.firstName} {row.player.lastName}
                      </Link>
                    )}
                  </th>
                  <td className="tabular px-3 text-right text-[16px] font-semibold">{row.points}</td>
                  <td className="tabular pl-3 pr-4 text-right text-[15px] text-ink-2">{row.handsWonVerified ? row.handsWon : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          {rows.some((row) => !row.handsWonVerified) ? <p className="mt-3 text-[12px] text-ink-3">{HANDS_WON_UNAVAILABLE}</p> : null}
          </>

          : <div className="min-w-0">
            {/*
              SORT BY ANY WEEK, OR THE TOTAL (owner, 2026-10-12). Tap a header — or, on a phone where
              the headers are small, choose here. Display only: nothing stored or ranked changes.
            */}
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <label htmlFor="weekly-sort" className="text-[13px] font-semibold">Sort by</label>
              <select
                id="weekly-sort"
                value={String(sort.key)}
                onChange={(e) => setSort({ key: e.target.value === 'total' ? 'total' : Number(e.target.value), dir: 'desc' })}
                className="min-h-11 rounded-md border border-control-border bg-paper px-3 text-[14px] focus-visible:border-deep"
              >
                <option value="total">Total</option>
                {sessions.map((session, index) => <option key={session.id} value={index}>Session {session.sessionNumber}</option>)}
              </select>
              <Button
                variant="secondary"
                onClick={() => setSort({ ...sort, dir: sort.dir === 'desc' ? 'asc' : 'desc' })}
                className="focus-visible:outline focus-visible:outline-2 focus-visible:outline-deep"
              >
                {sort.dir === 'desc' ? 'Highest first ▼' : 'Lowest first ▲'}
              </Button>
            </div>
            <div className="overflow-clip rounded-[14px] border border-light bg-paper">
            <div ref={weeklyHeader} aria-hidden="true" className="sticky top-0 z-20 overflow-hidden bg-paper">
              <div className="flex text-[11px] font-semibold uppercase tracking-[0.09em] text-ink-3" style={{width: weeklyWidth}}>
                <div className="sticky left-0 w-32 shrink-0 border-b border-light bg-paper py-4 pl-4 pr-3">Player</div>
                {/* Taps for the eye and the finger; the dropdown above is the keyboard's way in. */}
                {sessions.map((session, index) => (
                  <button key={session.id} type="button" tabIndex={-1} onClick={() => setSort(nextSort(sort, index))}
                    className={`w-20 shrink-0 whitespace-nowrap border-b border-light px-2 py-4 text-right uppercase tracking-[0.09em] ${sort.key === index ? 'text-ink' : 'hover:text-ink'}`}>
                    Session {session.sessionNumber}{sort.key === index ? (sort.dir === 'desc' ? ' ▼' : ' ▲') : ''}
                  </button>
                ))}
                <button type="button" tabIndex={-1} onClick={() => setSort(nextSort(sort, 'total'))}
                  className={`w-20 shrink-0 whitespace-nowrap border-b border-light px-2 py-4 text-right uppercase tracking-[0.09em] ${sort.key === 'total' ? 'text-ink' : 'hover:text-ink'}`}>
                  Total{sort.key === 'total' ? (sort.dir === 'desc' ? ' ▼' : ' ▲') : ''}
                </button>
              </div>
            </div>
            <div role="region" aria-label="Scores by session" tabIndex={0}
              onScroll={event => { if (weeklyHeader.current) weeklyHeader.current.scrollLeft = event.currentTarget.scrollLeft; }}
              className="relative max-w-full overflow-x-auto pb-2 focus-visible:outline-2 focus-visible:outline-offset-2">
              <table aria-label="Scores by session" className="table-fixed border-separate border-spacing-0 text-[14px]" style={{width: weeklyWidth}}>
                <colgroup><col style={{width:128}} />{sessions.map(session => <col key={session.id} style={{width:80}} />)}<col style={{width:80}} /></colgroup>
                <thead className="sr-only">
                  <tr className="text-left">
                    <th scope="col" className="sticky left-0 z-10 w-32 min-w-32 max-w-32 border-b border-stroke bg-chalk py-2 pr-3 text-xs font-semibold">Player</th>
                    {sessions.map((session, index) => (
                      <th scope="col" key={session.id} aria-sort={sort.key === index ? (sort.dir === 'desc' ? 'descending' : 'ascending') : undefined} className="min-w-20 whitespace-nowrap border-b border-stroke px-3 py-2 text-right text-xs font-semibold">Session {session.sessionNumber}</th>
                    ))}
                    <th scope="col" aria-sort={sort.key === 'total' ? (sort.dir === 'desc' ? 'descending' : 'ascending') : undefined} className="min-w-20 border-b border-stroke px-3 py-2 text-right text-xs font-semibold">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {sortStandings(rows, sort).map((row) => (
                    <tr key={row.playerId}>
                      <th scope="row" className="sticky left-0 z-10 w-32 min-w-32 max-w-32 break-words border-b border-light bg-paper py-4 pl-4 pr-3 text-left [overflow-wrap:anywhere]">
                        {nightsOnly
                          ? <span className="font-semibold text-ink">{row.player.firstName} {row.player.lastName}</span>
                          : <Link to={`/director/players/${row.playerId}`} className="font-semibold text-ink underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-4">{row.player.firstName} {row.player.lastName}</Link>}
                      </th>
                      {sessions.map((session, index) => (
                        <td key={session.id} className="tabular border-b border-light px-3 py-4 text-right text-ink-2">
                          {row.bySession[index] === null || row.bySession[index] === undefined
                            ? <span className="text-ink-3"><span aria-hidden="true">·</span><span className="sr-only">Not yet entered</span></span>
                            : row.bySession[index]}
                        </td>
                      ))}
                      <td className="tabular border-b border-light px-3 py-4 text-right font-semibold">{row.points}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </div>

          <p className="mt-3 text-[12px] text-ink-3">
            {/*
              Said plainly, because a director who counts 22 on the roster and 18 here will
              otherwise assume the table is broken. A count and not a list — she can see who on the
              roster tab, and a standings screen naming the people on nothing is a leaderboard of
              failure.
            */}
            {notShown > 0 ? (
              <>
                {notShown === 1
                  ? 'One player is not shown — they have not scored yet. '
                  : `${notShown} players are not shown — they have not scored yet. `}
              </>
            ) : null}
            A dot is a session not yet entered. A session someone missed scores zero, and a
            substitute&rsquo;s result stays with the substitute.
          </p>
          </div>}
        </div>
      )}
    </div>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={[
        'text-[11px] font-semibold uppercase tracking-[0.09em] text-ink-3',
        className ?? '',
      ].join(' ')}
    >
      {children}
    </th>
  );
}
